import { useState } from "react";
import { FormikProps } from "formik";
import { MediaModel } from "@/database";
import { addToHistory, handleHistoryNavigation } from "../../../../utils";
import { store } from "../../../../store";
import { aiService } from "@/store/app/app.actions";

interface UsePublishCardAiFieldsParams {
  formik: FormikProps<any>;
  initialdata: any;
  currentMediaDetails: MediaModel;
  isWizard: boolean;
  metaPresetData: any;
}

interface CustomPromptState {
  open: boolean;
  text: string;
  isSending: boolean;
}

interface CustomPrompt {
  title: CustomPromptState;
  description: CustomPromptState;
}

export const usePublishCardAiFields = ({
  formik,
  initialdata,
  currentMediaDetails,
  isWizard,
  metaPresetData,
}: UsePublishCardAiFieldsParams) => {
  const [activeField, setActiveField] = useState<
    "title" | "description" | null
  >(null);

  const [customPrompt, setCustomPrompt] = useState<CustomPrompt>({
    title: { open: false, text: "", isSending: false },
    description: { open: false, text: "", isSending: false },
  });

  const [descriptionHistory, setDescriptionHistory] = useState<string[]>([]);
  const [titleHistory, setTitleHistory] = useState<string[]>([]);
  const [currentDescriptionHistoryIndex, setCurrentDescriptionHistoryIndex] =
    useState(-1);
  const [currentTitleHistoryIndex, setCurrentTitleHistoryIndex] = useState(-1);
  const [isDescriptionBlurLoading, setIsDescriptionBlurLoading] =
    useState(false);
  const [isTitleBlurLoading, setIsTitleBlurLoading] = useState(false);
  const [isExcecuteTitleAiTrans, setIsExcecuteTitleAiTrans] = useState(false);
  const [isExcecuteDescriptionAiTrans, setIsExcecuteDescriptionAiTrans] =
    useState(false);
  const [isDescriptionAiButtonLoading, setIsDescriptionAiButtonLoading] =
    useState(false);
  const [isTitleAiButtonLoading, setIsTitleAiButtonLoading] = useState(false);

  const handleTitleHistoryNavigation = (direction: "back" | "forward") => {
    handleHistoryNavigation(
      direction,
      titleHistory,
      currentTitleHistoryIndex,
      setCurrentTitleHistoryIndex,
      "title",
      formik
    );
  };

  const handleDescriptionHistoryNavigation = (
    direction: "back" | "forward"
  ) => {
    handleHistoryNavigation(
      direction,
      descriptionHistory,
      currentDescriptionHistoryIndex,
      setCurrentDescriptionHistoryIndex,
      "desc",
      formik
    );
  };

  const handleCustomPromptClick = (field: "title" | "description") => {
    setCustomPrompt((prev) => ({
      ...prev,
      [field]: { ...prev[field], open: true },
    }));
  };

  const handleCustomPromptClose = (field: "title" | "description") => {
    setCustomPrompt((prev) => ({
      ...prev,
      [field]: { open: false, text: "", isSending: false },
    }));
  };

  const handleCustomPromptTextChange = (
    field: "title" | "description",
    text: string
  ) => {
    setCustomPrompt((prev) => ({
      ...prev,
      [field]: { ...prev[field], text },
    }));
  };

  const handlePromptSend = async (field: "title" | "description") => {
    if (!customPrompt[field].text.trim() || customPrompt[field].isSending)
      return;

    setCustomPrompt((prev) => ({
      ...prev,
      [field]: { ...prev[field], isSending: true },
    }));

    try {
      const response = await store.dispatch(
        aiService({
          title: formik.values[field === "description" ? "desc" : "title"],
          id: initialdata.media_id,
          service: "prompt",
          target_language: customPrompt[field].text,
        })
      );

      if (response) {
        const aiResponse = response.output_processed.output || "";

        if (field === "title") {
          if (
            titleHistory.length === 0 ||
            titleHistory[titleHistory.length - 1] !== formik.values.title
          ) {
            addToHistory(
              formik.values.title,
              setTitleHistory,
              setCurrentTitleHistoryIndex
            );
          }
          formik.setFieldValue("title", aiResponse);
          addToHistory(
            aiResponse,
            setTitleHistory,
            setCurrentTitleHistoryIndex
          );
        } else {
          if (
            descriptionHistory.length === 0 ||
            descriptionHistory[descriptionHistory.length - 1] !==
              formik.values.desc
          ) {
            addToHistory(
              formik.values.desc,
              setDescriptionHistory,
              setCurrentDescriptionHistoryIndex
            );
          }
          formik.setFieldValue("desc", aiResponse);
          addToHistory(
            aiResponse,
            setDescriptionHistory,
            setCurrentDescriptionHistoryIndex
          );
        }

        setCustomPrompt((prev) => ({
          ...prev,
          [field]: { ...prev[field], text: "", isSending: false },
        }));
      }
    } catch (error) {
      console.error("Error in AI service:", error);
      setCustomPrompt((prev) => ({
        ...prev,
        [field]: { ...prev[field], text: "", isSending: false },
      }));
    }
  };

  const handleTitleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    formik.handleBlur(e);
    if (!isWizard) return;

    const channelPreset = metaPresetData?.social?.filter((item: any) => {
      return initialdata?.channel_name === item?.channel_name;
    });
    const title_ai_value = channelPreset?.[0]?.title_ai_value;

    if (
      formik.values.title.trim() &&
      title_ai_value &&
      !isExcecuteTitleAiTrans
    ) {
      if (
        titleHistory.length === 0 ||
        titleHistory[titleHistory.length - 1] !== formik.values.title
      ) {
        addToHistory(
          formik.values.title,
          setTitleHistory,
          setCurrentTitleHistoryIndex
        );
      }

      setIsTitleBlurLoading(true);
      try {
        const response = await store.dispatch(
          aiService({
            title: formik.values.title,
            id: currentMediaDetails.id,
            service: "prompt",
            target_language: title_ai_value || "",
            type: "title",
          })
        );

        if (response) {
          const aiResponse = response.output_processed.output || "";
          formik.setFieldValue("title", aiResponse);
          addToHistory(
            aiResponse,
            setTitleHistory,
            setCurrentTitleHistoryIndex
          );
          setIsExcecuteTitleAiTrans(true);
        }
      } catch (error) {
        console.error("Error in AI service on title blur:", error);
      } finally {
        setIsTitleBlurLoading(false);
      }
    }
  };

  const handleDescriptionBlur = async (
    e: React.FocusEvent<HTMLTextAreaElement>
  ) => {
    formik.handleBlur(e);
    if (!isWizard) return;
    if (isExcecuteDescriptionAiTrans) return;

    const channelPreset = metaPresetData?.social?.filter((item: any) => {
      return initialdata?.channel_name === item?.channel_name;
    });
    const desc_ai_value = channelPreset?.[0]?.desc_ai_value;

    if (formik.values.desc.trim() && desc_ai_value) {
      if (
        descriptionHistory.length === 0 ||
        descriptionHistory[descriptionHistory.length - 1] !== formik.values.desc
      ) {
        addToHistory(
          formik.values.desc,
          setDescriptionHistory,
          setCurrentDescriptionHistoryIndex
        );
      }

      setIsDescriptionBlurLoading(true);
      try {
        const response = await store.dispatch(
          aiService({
            title: formik.values.desc,
            id: currentMediaDetails.id,
            service: "prompt",
            target_language: desc_ai_value || "",
            type: "description",
          })
        );

        if (response) {
          const aiResponse = response.output_processed.output || "";
          formik.setFieldValue("desc", aiResponse);
          addToHistory(
            aiResponse,
            setDescriptionHistory,
            setCurrentDescriptionHistoryIndex
          );
          setIsExcecuteDescriptionAiTrans(true);
        }
      } catch (error) {
        console.error("Error in AI service on description blur:", error);
      } finally {
        setIsDescriptionBlurLoading(false);
      }
    }
  };

  return {
    // shared
    activeField,
    setActiveField,
    customPrompt,
    handleCustomPromptClick,
    handleCustomPromptClose,
    handleCustomPromptTextChange,
    handlePromptSend,
    // title-specific
    titleHistory,
    setTitleHistory,
    currentTitleHistoryIndex,
    setCurrentTitleHistoryIndex,
    isTitleBlurLoading,
    setIsTitleBlurLoading,
    setIsDescriptionBlurLoading,
    setIsTitleAiButtonLoading,
    handleTitleHistoryNavigation,
    handleTitleBlur,
    // description-specific
    descriptionHistory,
    setDescriptionHistory,
    currentDescriptionHistoryIndex,
    setCurrentDescriptionHistoryIndex,
    isDescriptionBlurLoading,
    setIsDescriptionAiButtonLoading,
    handleDescriptionHistoryNavigation,
    handleDescriptionBlur,
    // internal flags (exposed in case they are needed later)
    isDescriptionAiButtonLoading,
    isTitleAiButtonLoading,
  };
};
